import { Component, HostBinding, OnInit, OnDestroy, ViewChild, ElementRef, Input, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, NavigationStart } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer } from '@angular/platform-browser';
import { NavigationService } from '../navigation.service';
import { ApiService } from '../api.service';
import { Observable } from 'rxjs/Observable'; 
import { BreakpointsService } from '../breakpoints.service';
import { Subscription } from 'rxjs/Subscription';
import { staticInOut } from '../router.animations';
import { uniqBy, orderBy, filter, map, flatten, pick, assign, findIndex, find, slice } from 'lodash';
import { LayoutService } from '../layout.service';
import { ImageGalleryService } from '../image-gallery.service';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';

@Component({
  selector: 'app-project-view',
  templateUrl: './project-view.component.html',
  styleUrls: ['./project-view.component.scss'],
  host: {
    '(scroll)': 'onScroll($event)',
    '[@staticInOut]': ''
  },
  animations: [
    staticInOut
  ]
})
export class ProjectViewComponent implements OnInit, OnDestroy {
  @Input() apiData;
  @Input() isLoadingApiData;
  @ViewChild('container') containerRef: ElementRef;
  title: string;
  category: string;
  subcategory: string;
  soundbite: string;
  description: string;
  rows: any[];
  previousUrl: string = null;
  index: number;
  projects: any = null;
  prevScrollTop: number = 0;
  breakpointSubscription: Subscription;
  imageGallerySubscriptionClose: Subscription;
  locationSubscription: any;
  pages: any[];
  parentUrl: string;
  isSidebarSticky: boolean;
  isProjectLoaded: boolean = false;
  private _projectsObs = new BehaviorSubject(null);
  public readonly projectsObs = this._projectsObs.asObservable();
  projectsSubscription: Subscription;

  constructor(
    private NavigationService: NavigationService,
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer,
    private router: Router,
    private apiService: ApiService,
    private elRef: ElementRef,
    private BreakpointsService: BreakpointsService,
    private LayoutService: LayoutService,
    private location: Location,
    private ImageGalleryService: ImageGalleryService
  ) {
    this.NavigationService.isSticky = true;
  }

  ngOnInit() {
    let subcategoryId;

    let parentRoute = this.route.snapshot;
    while (parentRoute.url.length === 0 && parentRoute.parent) {
      parentRoute = parentRoute.parent;
    }

    let urlMatch = /\/([^\/]+)\/([^\/]+)/.exec(window.location.pathname);
    let category = urlMatch[1];
    let subcategory = urlMatch[2];

    this.parentUrl = '/' + category + '/' + subcategory;

    if (typeof subcategory !== 'undefined') {
      // To get next/prev project
      this.projectsObs.skip(1).take(1).subscribe(projects => {
        this.index = findIndex(this.projects, {page_id: this.apiData.id});

        // If can't find project in this subcategory, then redirect to all
        if (this.index === -1 && subcategory !== 'all' && typeof this.route.snapshot.params['page'] !== 'undefined') {
          let page = this.route.snapshot.params['page'];
          this.router.navigate([`${category}/all/${page}`]);
        }
      });

      this.projects = null;
      this.getProjects(category, subcategory);
    }

    this.imageGallerySubscriptionClose = this.ImageGalleryService.onClose.skip(1).subscribe(evt => {
      this.setToPreviousScrollTop();
    });

    // On back need to check if need to close or open image gallery, or change image
    this.locationSubscription = this.location.subscribe((e) => {
      setTimeout(() => {
        this.setGalleryState();
      });
    });
  }

  getProjects(category, subcategory) {
    // From index page
    if (this.NavigationService.projectSource === 'index') {
      const indexRows = localStorage.getItem('index');
      if (typeof indexRows === 'undefined' || !indexRows) {
        // Remove the source
        this.NavigationService.projectSource = null;
      } else {
        this.projects = JSON.parse(indexRows);
      }
    }
    if (!this.projects) {
      // Don't have any saved thumb order
      // Get order from thumb page
      if (this.NavigationService.projectThumbs.length === 0) {
        this.getSubcategoryPage(category, subcategory).then(subcategoryPage => {
          let projectThumbs = subcategoryPage['thumbs'];
          if (typeof subcategoryPage['default_order_by'] !== 'undefined' && subcategoryPage['default_order_by']) {
            projectThumbs = orderBy(projectThumbs, [subcategoryPage['default_order_by']], [subcategoryPage['default_sort']]);
          }
          projectThumbs = uniqBy(projectThumbs, 'page_id');
          this.projects = projectThumbs;
          this._projectsObs.next(true);
        });
      // Know project order because have saved order
      // when clicked through from grid page
      } else {
        this.projects = this.NavigationService.projectThumbs;
        this._projectsObs.next(true);
      }
    } else {
      this._projectsObs.next(true);
    }
  }

  getSubcategoryPage(category, subcategory) {
    return this.apiService.getData(`pages/get_by_slug/${subcategory}`).then((data) => {
      let subcategoryPage;

      if (data['pages'].length > 1) {
        let categoryItem: any = find(window['categories'], { slug: category });
        subcategoryPage = find(data['pages'], { 'parent_id': categoryItem.id });
      } else if (data['pages'].length === 1) {
        subcategoryPage = data['pages'][0];
      }
      return subcategoryPage;
    });
  }

  ngOnDestroy() {
    if (this.isProjectLoaded) {
      this.LayoutService.saveGridScrollPosition(this.apiData.id, this.scrollTop);
    }
    if (typeof this.imageGallerySubscriptionClose !== 'undefined') {
      this.imageGallerySubscriptionClose.unsubscribe();
    }
    if (typeof this.locationSubscription !== 'undefined') {
      this.locationSubscription.unsubscribe();
    }
    if (typeof this.projectsSubscription !== 'undefined') {
      this.projectsSubscription.unsubscribe();
    }

    this.NavigationService.isSticky = false;
    this.NavigationService.isHiddenDesktop = false;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (typeof changes.apiData !== 'undefined' && changes.apiData.currentValue) {
      this.initProject();
    }
  }

  initGallery() {
    let imageRows = filter(this.rows, { single_content_type: 'content_images' });
    this.ImageGalleryService.gallery = map(flatten(map(imageRows, 'single_content')), 'image');
    this.setGalleryState();
  }

  setGalleryState() {
    // Timeout to prevent "Expression has changed after it was checked" exeption
    // on image gallery component within view-with-nav-bar
    setTimeout(() => {
      const indexMatch = /\/gallery\/(\d+)/.exec(window.location.pathname);
      
      // Gallery image index defined in url
      if (indexMatch !== null) {
        let galleryIndex = isNaN(parseInt(indexMatch[1])) ? null : parseInt(indexMatch[1]) - 1;
        
        // Not a valid image gallery number
        if (galleryIndex === null || galleryIndex >= this.ImageGalleryService.gallery.length) {
          this.router.navigate([`../../`], {relativeTo: this.route});
        // Valid image gallery number
        } else {
          // Go to image if gallery is open
          if (this.ImageGalleryService.isGalleryOpen) {
            this.ImageGalleryService.onChangeImageEvent(galleryIndex);
          // Open gallery if gallery is closed
          } else {
            this.ImageGalleryService.openMultiGallery(galleryIndex);
          }
        }

      // Gallery image index is not defined in url
      // but gallery is open
      } else if (this.ImageGalleryService.isGalleryOpen) {
        this.ImageGalleryService.closeGallery();
      }
    });
  }

  onImageClick(imageIndex) {
    this.LayoutService.saveGridScrollPosition(this.apiData.id, this.scrollTop);
    this.ImageGalleryService.openMultiGallery(imageIndex);
  }

  initProject() {
    let projectData: any = pick(this.apiData, ['title', 'slug', 'soundbite', 'description', 'rows', 'gallery']);
    projectData.rows = orderBy(projectData.rows, ['position'], ['asc']);

    this.rows = null;
    this.isProjectLoaded = false;

    setTimeout(() => {
      assign(this, projectData);

      this.initGallery();
      if (this.projects) {
        this.index = findIndex(this.projects, {'page_id': this.apiData.id});
      }
    });
  }

  get scrollTop() {
    return this.elRef.nativeElement.scrollTop || document.body.scrollTop;
  }

  onScroll($event) {
    let isScrollingDown = this.prevScrollTop < this.elRef.nativeElement.scrollTop;
    let container = this.elRef.nativeElement.querySelector('.container');
    let main = this.elRef.nativeElement.querySelector('.main');
    if (main) {
      let isScrolledPastNavBarHeight = main.getBoundingClientRect().top >= (this.NavigationService.navbarHeight - 40);
      this.prevScrollTop = this.elRef.nativeElement.scrollTop;
      if (isScrollingDown && !isScrolledPastNavBarHeight) {
        this.NavigationService.isHiddenDesktop = true;
      } else if (!isScrollingDown && isScrolledPastNavBarHeight) {
        this.NavigationService.isHiddenDesktop = false;
      }

      this.isSidebarSticky = container.getBoundingClientRect().top <= 0;
    }
  }

  navbarHeight() {
    return this.NavigationService.navbarHeight;
  }

  mod(n, m) {
    return ((n % m) + m) % m;
  }

  onNavigationEvent(eventName: string) {
    this.LayoutService.saveGridScrollPosition(this.apiData.id, this.scrollTop);
    if (!this.projects) {
      if (typeof this.projectsSubscription === 'undefined') {
        this.projectsSubscription = this.projectsObs.skip(1).subscribe(projects => {
          this.handleNavigationEvent(eventName);
        });
      }
    } else {
      this.handleNavigationEvent(eventName);
    }
  }

  handleNavigationEvent(eventName: string) {
    if (this.projects && this.projects.length > 0 && (eventName === 'next' || eventName === 'prev')) {
      let newIndex = eventName === 'next' ? this.index + 1 : this.index - 1;
      newIndex = this.mod(newIndex, this.projects.length);
      this.goToProjectWithIndex(newIndex);
    } else if (eventName === 'close') {
      this.close();
    }
  }

  goToProjectWithIndex(index: number) {
    let nextPageId = this.projects[index].page_id;
    let queryParams = { id: nextPageId };
    this.router.navigate([this.parentUrl], { queryParams: queryParams });
  }

  close() {
    this.NavigationService.onProjectClose();
    if (this.NavigationService.projectSource === 'index') {
      const indexParamsStr = localStorage.getItem('indexparams');
      let urlParams = {};
      if (typeof indexParamsStr !== 'undefined' && indexParamsStr) {
        const indexParams = JSON.parse(indexParamsStr);
        urlParams = {
          queryParams: {
            'sortBy': indexParams.sortKey,
            'sort': indexParams.sortAsc ? 'asc' : 'desc'
          }
        };
      }
      this.router.navigate(['project-index'], urlParams);
    } else if (this.NavigationService.projectSource === 'home') {
      this.router.navigate(['']);
    } else {
      this.router.navigate([this.parentUrl]);
    }
  }

  isContentLoaded() {
    this.isProjectLoaded = true;
    if (this.NavigationService.isBack) {
      this.setToPreviousScrollTop();
    }
  }

  setToPreviousScrollTop() {
    setTimeout(() => {
      this.elRef.nativeElement.scrollTop = this.LayoutService.getGridScrollPosition(this.apiData.id);
      document.body.scrollTop = this.LayoutService.getGridScrollPosition(this.apiData.id);
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/project-view/project-view.component.ts